import { useCallback, useEffect, useState } from "react";
import { Loader2, Users, Eye, Clock, Ticket, Repeat, LogIn, Bot, Smartphone, Globe, CalendarDays, RefreshCw, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { fmtDateTime, todayStr } from "@/lib/format";

const Bar = ({ label, count, max, tint = "bg-[#0052FF]" }) => (
  <div className="flex items-center gap-3">
    <div className="w-32 shrink-0 truncate text-xs font-medium text-slate-600" title={label}>{label}</div>
    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full rounded-full ${tint} transition-all`} style={{ width: `${max ? Math.max(4, (count / max) * 100) : 0}%` }} />
    </div>
    <div className="w-10 shrink-0 text-right text-xs font-bold text-slate-700">{count}</div>
  </div>
);

const Kpi = ({ icon: Icon, label, value, sub, tint = "text-[#0052FF]", testid }) => (
  <div className="card-soft p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs font-semibold text-slate-500"><Icon className={`h-4 w-4 ${tint}`} />{label}</div>
    <div className="mt-1 font-display text-2xl font-extrabold text-[#182678]">{value}</div>
    {sub && <div className="text-xs text-slate-400">{sub}</div>}
  </div>
);

const Card = ({ title, icon: Icon, children, testid }) => (
  <div className="card-soft p-5" data-testid={testid}>
    <div className="mb-4 flex items-center gap-2 font-display text-base font-bold text-[#182678]"><Icon className="h-4 w-4 text-[#0052FF]" />{title}</div>
    {children}
  </div>
);

const ROLE_TINT = { anonymous: "bg-slate-100 text-slate-600", customer: "bg-emerald-50 text-emerald-700", staff: "bg-blue-50 text-[#0052FF]", bot: "bg-rose-50 text-rose-600" };
const ROLE_LABEL = { anonymous: "Guest visitor", customer: "Passenger", staff: "Staff", bot: "Bot / crawler" };
const SCORE_TINT = { High: "bg-emerald-100 text-emerald-700", Medium: "bg-amber-100 text-amber-700", Low: "bg-slate-100 text-slate-500" };

export default function Analytics() {
  const [from, setFrom] = useState(todayStr(-6));
  const [to, setTo] = useState(todayStr());
  const [showBots, setShowBots] = useState(false);
  const [sum, setSum] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, v] = await Promise.all([
        api.get("/analytics/summary", { params: { from_date: from, to_date: to } }),
        api.get("/analytics/visitors", { params: { from_date: from, to_date: to, include_bots: showBots, limit: 300 } }),
      ]);
      setSum(s.data); setRows(v.data.rows);
    } finally { setLoading(false); }
  }, [from, to, showBots]);
  useEffect(() => { load(); }, [load]);

  const t = sum?.totals;
  const maxSrc = Math.max(1, ...(sum?.by_source || []).map((x) => x.count));
  const maxPage = Math.max(1, ...(sum?.top_pages || []).map((x) => x.count));
  const maxDay = Math.max(1, ...(sum?.by_day || []).map((x) => x.sessions));

  return (
    <div data-testid="analytics-page">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-[#182678]">Web analytics</h1>
          <p className="text-sm text-slate-500">Traffic, visitor sources, engagement &amp; booking conversion — live.</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label className="text-[11px] font-semibold text-slate-500">From</Label><Input type="date" className="h-10 w-40" value={from} max={to} onChange={(e) => setFrom(e.target.value)} data-testid="analytics-date-from" /></div>
          <div className="space-y-1"><Label className="text-[11px] font-semibold text-slate-500">To</Label><Input type="date" className="h-10 w-40" value={to} min={from} max={todayStr()} onChange={(e) => setTo(e.target.value)} data-testid="analytics-date-to" /></div>
          <div className="flex items-center gap-2 pb-2"><Switch checked={showBots} onCheckedChange={setShowBots} data-testid="analytics-bots-toggle" /><span className="text-xs font-medium text-slate-600">Include bots</span></div>
          <Button variant="outline" className="h-10 rounded-xl" onClick={load} data-testid="analytics-refresh"><RefreshCw className="h-4 w-4" />Refresh</Button>
        </div>
      </div>

      {loading || !t ? (
        <div className="grid h-64 place-items-center" data-testid="analytics-loading"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            <Kpi icon={Users} label="Visitors" value={t.visitors} sub={`${t.new_visitors} new · ${t.returning_visitors} returning`} testid="kpi-visitors" />
            <Kpi icon={Globe} label="Sessions" value={t.sessions} sub={`${t.pageviews} pageviews`} testid="kpi-sessions" />
            <Kpi icon={Clock} label="Avg. time" value={`${t.avg_minutes} min`} sub={`${t.bounce_rate}% bounce`} tint="text-indigo-500" testid="kpi-avg-time" />
            <Kpi icon={Ticket} label="Booked" value={`${t.conversion_rate}%`} sub={`${t.booked_sessions} sessions booked`} tint="text-emerald-600" testid="kpi-conversion" />
            <Kpi icon={Repeat} label="Return rate" value={`${t.return_rate}%`} sub="chance of coming back" tint="text-amber-600" testid="kpi-return-rate" />
            <Kpi icon={LogIn} label="Logged in" value={t.logged_in_sessions} sub={`${t.anonymous_sessions} anonymous`} tint="text-sky-600" testid="kpi-logged-in" />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card title="Where visitors come from" icon={Globe} testid="analytics-sources">
              <div className="space-y-2.5">
                {sum.by_source.length ? sum.by_source.map((s) => <Bar key={s.label} label={s.label} count={s.count} max={maxSrc} />) : <p className="text-sm text-slate-400">No traffic yet.</p>}
              </div>
            </Card>
            <Card title="Most viewed pages" icon={Eye} testid="analytics-pages">
              <div className="space-y-2.5">
                {sum.top_pages.length ? sum.top_pages.map((p) => <Bar key={p.label} label={p.label} count={p.count} max={maxPage} tint="bg-indigo-500" />) : <p className="text-sm text-slate-400">No pageviews yet.</p>}
              </div>
            </Card>
            <Card title="Traffic by day" icon={CalendarDays} testid="analytics-by-day">
              <div className="space-y-2.5">
                {sum.by_day.length ? sum.by_day.map((d) => <Bar key={d.day} label={fmtDateTime(d.day + "T00:00:00").split(",")[0]} count={d.sessions} max={maxDay} tint="bg-emerald-500" />) : <p className="text-sm text-slate-400">No traffic yet.</p>}
              </div>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-[1fr_2fr]">
            <div className="grid grid-cols-2 gap-4">
              <Card title="Visitor type" icon={Users} testid="analytics-roles">
                <div className="space-y-2">
                  {sum.by_role.map((r) => (
                    <div key={r.label} className="flex items-center justify-between text-sm">
                      <span className={`chip ${ROLE_TINT[r.label] || "bg-slate-100 text-slate-600"}`}>{ROLE_LABEL[r.label] || r.label}</span>
                      <b className="text-slate-700">{r.count}</b>
                    </div>
                  ))}
                </div>
              </Card>
              <Card title="Device" icon={Smartphone} testid="analytics-devices">
                <div className="space-y-2">
                  {sum.by_device.map((d) => (
                    <div key={d.label} className="flex items-center justify-between text-sm">
                      <span className="font-medium text-slate-600">{d.label}</span><b className="text-slate-700">{d.count}</b>
                    </div>
                  ))}
                  {t.bot_sessions > 0 && <div className="flex items-center justify-between border-t pt-2 text-sm text-rose-600"><span className="flex items-center gap-1 font-medium"><Bot className="h-3.5 w-3.5" />Bots filtered</span><b>{t.bot_sessions}</b></div>}
                </div>
              </Card>
            </div>
            <Card title="Live visitor log" icon={MapPin} testid="analytics-visitor-log">
              <div className="-mx-2 max-h-[460px] overflow-auto">
                <table className="w-full text-left text-sm">
                  <thead className="sticky top-0 bg-white text-xs uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="p-2">Visitor</th><th className="p-2">Source</th><th className="p-2">Entry</th>
                      <th className="p-2 text-center">Pages</th><th className="p-2 text-center">Time</th><th className="p-2 text-center">Booked</th>
                      <th className="p-2">Return</th><th className="p-2 whitespace-nowrap">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.session_id} className="border-b border-slate-50" data-testid={`visitor-row-${i}`}>
                        <td className="p-2">
                          <div className="font-mono text-xs font-semibold text-slate-700">{r.ip || "—"}</div>
                          <div className="flex items-center gap-1">
                            <span className={`chip ${ROLE_TINT[r.role]}`}>{r.user_name || ROLE_LABEL[r.role]}</span>
                            <span className="text-[10px] text-slate-400">{r.device}</span>
                          </div>
                        </td>
                        <td className="p-2 text-slate-600">{r.source}</td>
                        <td className="p-2 max-w-[140px] truncate text-slate-500" title={r.paths.join(" → ")}>{r.entry}</td>
                        <td className="p-2 text-center font-semibold">{r.pageviews}</td>
                        <td className="p-2 text-center text-slate-600">{r.minutes}m</td>
                        <td className="p-2 text-center">{r.booked ? <span className="chip bg-emerald-100 text-emerald-700">Yes</span> : <span className="text-slate-300">—</span>}</td>
                        <td className="p-2"><span className={`chip ${SCORE_TINT[r.return_label]}`}>{r.return_label} {r.returning ? `· ${r.visits}×` : ""}</span></td>
                        <td className="p-2 whitespace-nowrap text-xs text-slate-400">{fmtDateTime(r.started_at)}</td>
                      </tr>
                    ))}
                    {!rows.length && <tr><td colSpan={8} className="p-6 text-center text-slate-400">No visitors recorded in this range yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
